import { useState, useRef, useCallback } from "react";
import { animate } from "animejs";

// Order represents rotation sequence: position 0 → 1 → 2 → 3 → 0
export const COMPONENT_ORDER = ["aktuell", "ausblick", "info", "archiv"];

// Initial absolute rotations matching the CSS transforms in styles.css
const INITIAL_ROTATIONS = {
  aktuell: 0,
  ausblick: 90,
  info: 0,
  archiv: 90,
};

// Panels that have a constant scale(-1,-1) flip applied
const FLIPPED_PANELS = new Set(["ausblick", "info"]);

/**
 * React hook – manages cross-panel rotation state.
 *
 * Returns:
 *   currentPosition      – index into COMPONENT_ORDER for the visible panel
 *   isAnimating          – true while a rotation is in flight
 *   setPanelRef          – callback-ref factory: <div ref={setPanelRef("aktuell")} />
 *   navigateTo           – animated transition to a named section
 *   jumpToPosition       – INSTANT jump (no animation) – used by videos 2-4 to
 *                          start at the end state of the previous video
 *   navigateFromDefault  – start from position 0 regardless of current state
 */
export const useCrossRotation = () => {
  const [currentPosition, setCurrentPosition] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Refs so callbacks always read fresh values without stale closures
  const currentPositionRef = useRef(0);
  const isAnimatingRef = useRef(false);
  const queuedNavigation = useRef(null);

  // Persists absolute rotation per panel – avoids floating-point drift
  const absoluteRotations = useRef({ ...INITIAL_ROTATIONS });

  // DOM element refs for each panel
  const panelRefs = useRef({});

  /** Callback-ref factory: <div ref={setPanelRef("aktuell")} /> */
  const setPanelRef = (name) => (el) => {
    panelRefs.current[name] = el;
  };

  // ── Animated rotation ─────────────────────────────────────────────────────
  const runRotation = useCallback(
    (targetComponent, fromPosition, onComplete) => {
      const targetIndex = COMPONENT_ORDER.indexOf(targetComponent);
      if (targetIndex === -1) return;

      const startPosition =
        fromPosition !== undefined ? fromPosition : currentPositionRef.current;
      const rotations = (targetIndex - startPosition + 4) % 4;

      if (rotations === 0) {
        currentPositionRef.current = targetIndex;
        setCurrentPosition(targetIndex);
        onComplete?.();
        return;
      }

      isAnimatingRef.current = true;
      setIsAnimating(true);

      const durationPerRotation = 1000 / rotations;
      const totalDuration = rotations * durationPerRotation;

      COMPONENT_ORDER.forEach((component) => {
        const el = panelRefs.current[component];
        if (!el) return;

        const isFlipped = FLIPPED_PANELS.has(component);

        for (let i = 0; i < rotations; i++) {
          const stepTarget =
            absoluteRotations.current[component] + (i + 1) * 90;

          setTimeout(() => {
            animate(el, {
              rotate: stepTarget,
              ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
              duration: durationPerRotation,
              easing: "easeOutExpo",
            });
          }, i * durationPerRotation);
        }
      });

      // Update tracked rotations immediately so queued nav sees correct values
      COMPONENT_ORDER.forEach((c) => {
        absoluteRotations.current[c] += rotations * 90;
      });

      setTimeout(() => {
        currentPositionRef.current = targetIndex;
        setCurrentPosition(targetIndex);
        isAnimatingRef.current = false;
        setIsAnimating(false);

        // Process any navigation queued during this animation
        if (queuedNavigation.current) {
          const next = queuedNavigation.current;
          queuedNavigation.current = null;
          runRotation(next);
        }

        onComplete?.();
      }, totalDuration);
    },
    []
  );

  const navigateTo = useCallback(
    (targetComponent, onComplete) => {
      if (isAnimatingRef.current) {
        queuedNavigation.current = targetComponent;
        return;
      }
      runRotation(targetComponent, undefined, onComplete);
    },
    [runRotation]
  );

  // ── Instant jump (no animation) ──────────────────────────────────────────
  // Advances rotation state by `steps` × 90° and immediately applies the CSS
  // transforms so the panels appear at the target position on the very first
  // frame.  Used by videos 2-4 to begin at the end state of the prior video.
  const jumpToPosition = useCallback(
    (targetComponent) => {
      const targetIndex = COMPONENT_ORDER.indexOf(targetComponent);
      if (targetIndex === -1) return;

      const steps =
        (targetIndex - currentPositionRef.current + 4) % 4;
      if (steps === 0) {
        currentPositionRef.current = targetIndex;
        setCurrentPosition(targetIndex);
        return;
      }

      // Advance absolute rotations
      COMPONENT_ORDER.forEach((c) => {
        absoluteRotations.current[c] += steps * 90;
      });

      // Use animate with duration:0 so anime.js updates its internal state.
      // Directly setting el.style.transform would bypass anime's tracking and
      // cause the subsequent animated rotation to travel the wrong distance.
      COMPONENT_ORDER.forEach((component) => {
        const el = panelRefs.current[component];
        if (!el) return;

        const isFlipped = FLIPPED_PANELS.has(component);
        const rot = absoluteRotations.current[component];
        animate(el, {
          rotate: rot,
          ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
          duration: 0,
        });
      });

      currentPositionRef.current = targetIndex;
      setCurrentPosition(targetIndex);
    },
    []
  );

  /** Navigate starting from position 0 (e.g. on initial route entry) */
  const navigateFromDefault = useCallback(
    (targetComponent) => {
      currentPositionRef.current = 0;
      absoluteRotations.current = { ...INITIAL_ROTATIONS };
      runRotation(targetComponent, 0);
    },
    [runRotation]
  );

  /** Reset all state and rotations back to initial values */
  const reset = useCallback(() => {
    currentPositionRef.current = 0;
    setCurrentPosition(0);
    absoluteRotations.current = { ...INITIAL_ROTATIONS };
    isAnimatingRef.current = false;
    setIsAnimating(false);
    queuedNavigation.current = null;
  }, []);

  return {
    currentPosition,
    isAnimating,
    setPanelRef,
    navigateTo,
    jumpToPosition,
    navigateFromDefault,
    reset,
    currentPositionRef,
  };
};
